import React, { useRef, useMemo, memo } from 'react';
import PropTypes from 'prop-types';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import json from 'react-syntax-highlighter/dist/cjs/languages/prism/json';
import markup from 'react-syntax-highlighter/dist/cjs/languages/prism/markup';
import animateScrollTo from 'animated-scroll-to';
import escape from 'lodash.escape';
import { InlineAlert } from '@saucelabs/ds-alert';
import { Button } from '@saucelabs/ds-button';
import { NoData } from '@saucelabs/ds-data-states';
import {
  IconFileFill,
  IconDownloadFill,
  IconVerticalAlignBottomFill,
  IconVerticalAlignTopFill,
} from '@saucelabs/ds-icons';
import { sanitizeHTML } from '@saucelabs/sl-util-helpers';

import Styles from './JobLog.styles';
import { automator, logcat, seleniumserver } from './languages';
import getLogConfig from './getLogConfig';
import * as fileConstants from '../../ducks/file/constants';

const languages = {
  // default languages
  json,
  xml: markup,
  markup,
  // custom languages
  automator,
  logcat,
  seleniumserver,
};

const DEFAULT_MAX_COLOR_LOG_SIZE = 100000; // 100kB
const DEFAULT_MAX_DISPLAYED_LOG_LINES = 100000;
const DEFAULT_MAX_DISPLAYED_LOG_SIZE = 25000000; // 25MB

Object.entries(languages).forEach(([key, val]) => {
  SyntaxHighlighter.registerLanguage(key, val);
});

const lineNumberStyles = {
  textAlign: 'right',
  float: 'left',
  paddingRight: '11px',
};

const JobLog = memo((props) => {
  const {
    file: { data, lines, status },
    fileName,
    maxColorLogSize,
    maxDisplayedLogLines,
    maxDisplayedLogSize,
    onDownloadClick,
    showLineNumbers,
    href,
  } = props;
  const { language, data: content } = useMemo(() => getLogConfig(fileName, data), [fileName, data]);
  const { length } = content;

  const isTooLarge = length > maxDisplayedLogSize || lines > maxDisplayedLogLines;
  const isNotLoaded = status === fileConstants.STATUS.FAILURE;
  const shouldSkipHighlighting = language === 'text' || length > maxColorLogSize;

  const shouldSanitize = !isTooLarge && shouldSkipHighlighting;

  const containerRef = useRef();
  const cleanContent = useMemo(() => {
    if (shouldSanitize) {
      return sanitizeHTML(escape(content));
    }
    return '';
  }, [content, shouldSanitize]);

  const scrollToBottom = () => {
    if (containerRef.current) {
      animateScrollTo(containerRef.current.scrollHeight, {
        elementToScroll: containerRef.current,
      });
    }
  };
  const scrollToTop = () => {
    if (containerRef.current) {
      animateScrollTo(0, {
        elementToScroll: containerRef.current,
      });
    }
  };

  if (isNotLoaded) {
    return (
      <div className="w-full text-center pt-0 pb-0 pr-1 pl-1">
        <InlineAlert
          className="mb-1"
          color="warning"
        >
          The log file is too long to display.
        </InlineAlert>
        <a href={href}>
          <Button
            color="primary"
            prefixIcon={<IconDownloadFill />}
            variant="text"
          >
            Download File
          </Button>
        </a>
      </div>
    );
  }

  if (isTooLarge) {
    return (
      <div className="w-full text-center pt-0 pb-0 pr-1 pl-1">
        <InlineAlert
          className="mb-1"
          color="warning"
        >
          The log file is too long to display.
        </InlineAlert>
        <Button
          color="primary"
          onPress={onDownloadClick}
          prefixIcon={<IconDownloadFill />}
          variant="text"
        >
          Download File
        </Button>
      </div>
    );
  }

  if (status === fileConstants.STATUS.SKIPPED) {
    return (
      <div className="w-full flex items-center flex-col">
        <NoData
          action={(
            <Button
              onPress={onDownloadClick}
              prefixIcon={<IconDownloadFill />}
              size="md"
            >
              {`Download ${fileName}`}
            </Button>
          )}
          description="Download the file to view it locally"
          icon={IconFileFill}
          spacing="large"
          title="File type is not viewable"
        />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={Styles.default}
    >
      <button
        className={`inline-flex p-0.5 right-1 top-1 rounded text-white ${Styles['go-to-bottom']}`}
        onClick={scrollToBottom}
        type="button"
      >
        <IconVerticalAlignBottomFill className="fill-current text-white w-2 h-2" />
        Go to bottom
      </button>
      {shouldSkipHighlighting ? (
        <pre
          className="pt-0 pb-0 pl-1.5 pr-1.5"
          /* eslint-disable-next-line react/no-danger */
          dangerouslySetInnerHTML={{ __html: cleanContent }}
        />
      ) : (
        <SyntaxHighlighter
          language={language}
          lineNumberContainerStyle={lineNumberStyles}
          showLineNumbers={showLineNumbers}
          useInlineStyles={false}
        >
          {content}
        </SyntaxHighlighter>
      )}
      <button
        className={`inline-flex p-0.5 right-1 bottom-1 rounded text-white ${Styles['go-to-top']}`}
        onClick={scrollToTop}
        type="button"
      >
        <IconVerticalAlignTopFill className="fill-current text-white w-2 h-2" />
        Go to top
      </button>
    </div>
  );
});

JobLog.displayName = 'JobLog';

JobLog.propTypes = {
  file: PropTypes.shape({
    data: PropTypes.any.isRequired,
    lines: PropTypes.number,
    status: PropTypes.string,
  }).isRequired,
  fileName: PropTypes.string.isRequired,
  href: PropTypes.string,
  maxColorLogSize: PropTypes.number,
  maxDisplayedLogLines: PropTypes.number,
  maxDisplayedLogSize: PropTypes.number,
  onDownloadClick: PropTypes.func.isRequired,
  showLineNumbers: PropTypes.bool,
};

JobLog.defaultProps = {
  href: '',
  maxColorLogSize: DEFAULT_MAX_COLOR_LOG_SIZE,
  maxDisplayedLogLines: DEFAULT_MAX_DISPLAYED_LOG_LINES,
  maxDisplayedLogSize: DEFAULT_MAX_DISPLAYED_LOG_SIZE,
  showLineNumbers: true,
};

export default JobLog;
